import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, ArrowLeftRight, Plus, ScanText, Search, Trash2, X } from "lucide-react"
import { useEffect, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { type Articulo, FormularioArticulo } from "@/componentes/inventario/FormularioArticulo"
import { FichaOrden } from "@/componentes/ordenes/FichaOrden"
import { LectorFactura } from "@/componentes/ordenes/LectorFactura"
import { cantidadValida, precioLineaValido, subtotalCentimos } from "@/componentes/ordenes/lineas"
import { ServiciosParaAnadir } from "@/componentes/ordenes/ServiciosParaAnadir"
import { type Alerta, type Indicadores, TableroOrdenes } from "@/componentes/ordenes/TableroOrdenes"
import type { EstadoOrden, Orden } from "@/componentes/ordenes/tipos"
import { ESTADOS, TONO_ESTADO } from "@/componentes/ordenes/tipos"
import { Conversor } from "@/componentes/tesoreria/Conversor"
import { comoMonto } from "@/componentes/tesoreria/dinero"
import { monedasDeTasas, type TasaVigente } from "@/componentes/tesoreria/tasas"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import {
  limitesDe,
  nombreDe,
  type Periodo,
  SelectorPeriodo,
} from "@/componentes/ui/SelectorPeriodo"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha, urgenciaDe } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"
import { Campo, crearCategoria } from "@/paginas/interno/Gastos"

/** Cuántas se traen de golpe. El botón de abajo pide otra tanda igual. */
const POR_TANDA = 100

type Proveedor = { id: number; nombre: string; rif: string | null }
type Divisa = { id: number; codigo: string; nombre: string }
type Categoria = { id: number; nombre: string; activo: boolean }

/**
 * Bandeja de órdenes de pago administrativas.
 *
 * ⚠ **Aprobar y ejecutar son permisos distintos, y esa separación es la razón de ser
 * de esta pantalla.** Quien autoriza el pago no lo hace en el banco; la orden guarda
 * las dos firmas. Esconder un botón no autoriza nada: lo decide el servidor.
 *
 * ⚠ **El desglose fiscal se congela al enviar a aprobación.** Por eso los impuestos
 * sólo se tocan en borrador: quien aprueba tiene que estar viendo exactamente lo que
 * se le va a transferir al proveedor.
 *
 * ⚠ **Esta pantalla no es alcanzable desde el portal.** El servidor responde 404 a un
 * usuario `CLIENTE` antes de mirar el permiso: el gasto administrativo es el margen
 * de la agencia (regla 19).
 */
const NOMBRE_ALERTA: Record<Alerta, string> = {
  vencidas: "vencidas",
  por_vencer: "por vencer",
  sin_factura: "sin factura",
  sin_numero: "sin número",
}

/**
 * La bandeja como documento exportable, con las filas ya filtradas de la pantalla.
 *
 * ⚠ **No se lee de la tabla**: los importes se pintan crudos («1234.56 USD») y mezclan
 * monedas, y «Vence» lleva debajo la urgencia. Aquí la moneda va en su columna y cada
 * importe es un número.
 */
function documentoBandeja(filas: Orden[], subtitulo: string): DocumentoExportable {
  const dia = (t: string | null) => (t ? t.slice(0, 10) : null)
  return {
    titulo: "Órdenes de pago",
    subtitulo,
    secciones: [
      {
        titulo: "Órdenes de pago administrativas",
        columnas: [
          { titulo: "Código", tipo: "texto" },
          { titulo: "Emisión", tipo: "fecha" },
          { titulo: "Proveedor", tipo: "texto" },
          { titulo: "Descripción", tipo: "texto" },
          { titulo: "Categoría", tipo: "texto" },
          { titulo: "Estado", tipo: "texto" },
          { titulo: "Vence", tipo: "fecha" },
          { titulo: "Moneda", tipo: "texto" },
          { titulo: "Base", tipo: "monto" },
          { titulo: "A pagar", tipo: "monto" },
        ],
        filas: filas.map((o) => ({
          celdas: [
            o.codigo,
            dia(o.fecha_emision),
            o.proveedor,
            o.concepto,
            o.categoria,
            ESTADOS.find((e) => e.valor === o.estado)?.etiqueta ?? o.estado,
            dia(o.fecha_vencimiento),
            o.divisa,
            Number(o.monto_base),
            Number(o.monto_neto),
          ],
        })),
      },
    ],
  }
}

export function OrdenesPago() {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const [filtro, setFiltro] = useState<string>("")
  const [alerta, setAlerta] = useState<Alerta | null>(null)
  const [proveedorId, setProveedorId] = useState<number | null>(null)
  const [texto, setTexto] = useState("")
  const [buscar, setBuscar] = useState("")
  const [abierta, setAbierta] = useState<number | null>(null)
  const [creando, setCreando] = useState(false)
  // Arranca en TODOS y no en el mes: la bandeja se abre para ver qué hay que pagar,
  // y lo que está por pagar no siempre se emitió este mes. El período es para buscar
  // hacia atrás, no para recortar lo de hoy.
  const [periodo, setPeriodo] = useState<Periodo>(null)
  const [tope, setTope] = useState(POR_TANDA)

  const puedeCrear = tienePermiso(usuario, "orden_pago:crear")
  // Manda la factura a Google: un permiso propio, sólo del superadministrador.
  const puedeLeerFactura = tienePermiso(usuario, "factura_ia:usar")
  const [leyendo, setLeyendo] = useState(false)

  // Se busca al dejar de teclear, no en cada letra: la búsqueda es del servidor, y
  // una petición por pulsación haría parpadear la tabla mientras se escribe.
  useEffect(() => {
    const espera = window.setTimeout(() => setBuscar(texto.trim()), 300)
    return () => window.clearTimeout(espera)
  }, [texto])

  const limites = limitesDe(periodo)
  const parametros = new URLSearchParams({ limit: String(tope) })
  if (limites) {
    parametros.set("desde", limites.desde)
    parametros.set("hasta", limites.hasta)
  }
  if (filtro) parametros.set("estado", filtro)
  if (alerta) parametros.set("alerta", alerta)
  if (proveedorId !== null) parametros.set("proveedor_id", String(proveedorId))
  if (buscar) parametros.set("buscar", buscar)
  const hayFiltros = Boolean(filtro || alerta || proveedorId !== null || buscar || periodo)

  const ordenes = useQuery({
    queryKey: ["ordenes-pago", { filtro, alerta, proveedorId, buscar, periodo, tope }],
    queryFn: () => api.get<Orden[]>(`/api/v1/ordenes-pago?${parametros.toString()}`),
    // ⚠ Sin esto, cada cambio de filtro vaciaba la PANTALLA ENTERA —tarjetas incluidas—
    // mientras llegaba la respuesta: la bandeja parpadeaba a «Cargando…» con cada clic
    // en una tarjeta. Así se queda lo anterior hasta que llega lo nuevo.
    placeholderData: keepPreviousData,
  })
  // Bajo la misma raíz `ordenes-pago`: toda mutación que invalida la bandeja refresca
  // también las tarjetas, sin tener que acordarse de hacerlo en cada una.
  const indicadores = useQuery({
    queryKey: ["ordenes-pago", "indicadores"],
    queryFn: () => api.get<Indicadores>("/api/v1/ordenes-pago/indicadores"),
  })

  function limpiarFiltros() {
    setFiltro("")
    setAlerta(null)
    setProveedorId(null)
    setTexto("")
    setBuscar("")
    setPeriodo(null)
    setTope(POR_TANDA)
  }
  const proveedores = useQuery({
    queryKey: ["proveedores", "ADMINISTRATIVO"],
    queryFn: () =>
      api.get<Proveedor[]>("/api/v1/proveedores?limit=100&activo=true&categoria=ADMINISTRATIVO"),
    // ⚠ **Con `orden_pago:crear` también**: quien redacta órdenes de un expediente no
    // tiene el catálogo entero, y el servidor le abre estas lecturas para el formulario.
    enabled: tienePermiso(usuario, "proveedor:ver") || puedeCrear,
  })
  const divisas = useQuery({
    queryKey: ["divisas"],
    queryFn: () => api.get<Divisa[]>("/api/v1/divisas"),
    enabled: tienePermiso(usuario, "tesoreria:ver") || puedeCrear,
  })
  // Para el conversor de cada línea. Llegan en la lectura natural —bolívares por
  // una unidad—, que es la única con la que se convierte a mano.
  const tasas = useQuery({
    queryKey: ["tasas-vigentes"],
    queryFn: () => api.get<TasaVigente[]>("/api/v1/tasas/vigentes"),
  })
  const categorias = useQuery({
    queryKey: ["categorias-gasto"],
    queryFn: () => api.get<Categoria[]>("/api/v1/categorias-gasto"),
    enabled: tienePermiso(usuario, "gasto:ver") || puedeCrear,
  })

  const crear = useMutation({
    mutationFn: (cuerpo: Record<string, unknown>) =>
      api.post<Orden>("/api/v1/ordenes-pago", cuerpo),
    onSuccess: (orden) => {
      clienteQuery.invalidateQueries({ queryKey: ["ordenes-pago"] })
      setCreando(false)
      setAbierta(orden.id)
    },
  })

  if (ordenes.isPending && !ordenes.data) {
    return <p className="py-6">Cargando…</p>
  }
  if (ordenes.isError) {
    return (
      <p className="flex items-center gap-2 py-6" style={{ color: "var(--destructive)" }}>
        <AlertCircle size={16} /> No se pudieron cargar las órdenes de pago.
      </p>
    )
  }

  const filas = ordenes.data ?? []
  const error = crear.error instanceof ErrorApi ? crear.error.message : null

  return (
    <section className="space-y-4">
      {/* ⚠ **Esto NO se queda pegado arriba, y se intentó.** El tablero de indicadores
          y los filtros estuvieron en un `.encabezado-pegajoso`: en esta pantalla eso
          son más de trescientos píxeles de banda fija, así que al bajar tapaba media
          tabla y las filas pasaban por detrás. La página se desplaza entera; para
          volver arriba se sube. */}
      <div className="space-y-3">
        {/* Ni título ni frase propios: los pone la cabecera de «Administración». Aquí
            arriba sólo van las acciones, a la derecha, como en las vistas de Tesorería. */}
        <header className="flex flex-wrap items-start justify-end gap-2">
          <Exportar
            titulo="Órdenes de pago"
            documento={() =>
              documentoBandeja(
                filas,
                [
                  nombreDe(periodo),
                  filtro &&
                    `Estado: ${ESTADOS.find((e) => e.valor === filtro)?.etiqueta ?? filtro}`,
                  alerta && `Alerta: ${NOMBRE_ALERTA[alerta]}`,
                  proveedorId !== null &&
                    `Proveedor: ${indicadores.data?.proveedores.find((p) => p.proveedor_id === proveedorId)?.proveedor ?? proveedorId}`,
                  buscar && `Búsqueda: «${buscar}»`,
                  `${filas.length} ${filas.length === 1 ? "orden" : "órdenes"}`,
                ]
                  .filter(Boolean)
                  .join(" · "),
              )
            }
          />
          {/* La factura se lee aquí mismo, donde nace la orden: es la misma alta,
              con lo impreso transcrito en vez de tecleado. */}
          {puedeLeerFactura && (
            <Boton onClick={() => setLeyendo(true)}>
              <ScanText size={16} /> Leer factura con IA
            </Boton>
          )}
          {puedeCrear && (
            <Boton variante="primario" onClick={() => setCreando(true)}>
              <Plus size={16} /> Nueva orden
            </Boton>
          )}
        </header>

        {/* ⚠ **Dicho, no supuesto.** Sin `orden_pago:ver_todas` el servidor recorta la
            bandeja y las tarjetas a lo que uno redactó; sin esta línea, quien la mira
            creería que la agencia tiene tres órdenes. */}
        {!tienePermiso(usuario, "orden_pago:ver_todas") && (
          <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
            Ves sólo las órdenes que redactaste tú.
          </p>
        )}

        {indicadores.data && (
          <TableroOrdenes
            datos={indicadores.data}
            estado={filtro}
            alerta={alerta}
            proveedorId={proveedorId}
            onEstado={setFiltro}
            onAlerta={setAlerta}
            onProveedor={setProveedorId}
          />
        )}
        {indicadores.isError && (
          <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
            No se pudieron cargar los indicadores; la bandeja de abajo sigue al día.
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {/* El período, primero: es el filtro que acota una lista que crece sin parar.
            Por FECHA DE EMISIÓN de la factura, el mismo criterio que el libro de
            compras y el informe de gastos — con tres pantallas contando el mismo mes
            de dos maneras, ninguna de las tres es creíble. */}
          <SelectorPeriodo
            valor={periodo}
            onCambiar={(p) => {
              setPeriodo(p)
              setTope(POR_TANDA)
            }}
          />
          <label className="relative min-w-[14rem] flex-1 sm:max-w-sm">
            <span className="sr-only">Buscar por código o descripción</span>
            <Search
              size={16}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
              style={{ color: "var(--fg-muted)" }}
              aria-hidden="true"
            />
            <input
              type="search"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Código o descripción…"
              className="campo w-full pl-8"
            />
          </label>
          <div className="flex flex-wrap gap-1">
            <Boton
              tamano="sm"
              variante={filtro === "" ? "primario" : "fantasma"}
              onClick={() => setFiltro("")}
            >
              Todas
            </Boton>
            {ESTADOS.map((estado) => (
              <Boton
                key={estado.valor}
                tamano="sm"
                variante={filtro === estado.valor ? "primario" : "fantasma"}
                onClick={() => setFiltro(estado.valor)}
              >
                {estado.etiqueta}
                {indicadores.data && (
                  <span className="ml-1 tabular-nums opacity-70">
                    {indicadores.data.por_estado[estado.valor].cantidad}
                  </span>
                )}
              </Boton>
            ))}
          </div>
          {/* ⚠ Los filtros se combinan —«por pagar» de un proveedor, vencidas que
            contienen «factura»—, y sin un «quitar» a la vista, quien llega a una tabla
            vacía no sabe cuál de los cuatro la está vaciando. */}
          {hayFiltros && (
            <Boton tamano="sm" variante="fantasma" onClick={limpiarFiltros}>
              <X size={14} /> Quitar filtros
            </Boton>
          )}
          <span className="ml-auto text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
            {filas.length} {filas.length === 1 ? "orden" : "órdenes"}
            {ordenes.isFetching && " · actualizando…"}
          </span>
        </div>
      </div>

      <Tabla etiqueta="Órdenes de pago administrativas">
        <Cabecera>
          <Th>Código</Th>
          {/* ⚠ **La fecha de EMISIÓN de la factura, que es por la que se ordena** y la
              que decide el mes en el libro de compras y en el informe. La de
              vencimiento tiene su propia columna, y la del pago vive en la ficha:
              mezclarlas en una sola haría que la bandeja se leyera distinto según la
              fila. Lo que todavía no tiene factura dice cuándo se redactó. */}
          <Th>Fecha</Th>
          <Th>Proveedor</Th>
          {/* ⚠ **«Descripción» y no «concepto», y no es sinónimo.** Los conceptos son
              las LÍNEAS, que viven en la ficha; esto es cómo se reconoce el documento
              en una lista de cincuenta: «Factura N.º 5615616». Llamarlo concepto
              invitaba a repetir aquí lo que ya está dentro. */}
          <Th>Descripción</Th>
          <Th>Estado</Th>
          <Th>Vence</Th>
          <Th derecha>Base</Th>
          <Th derecha>A pagar</Th>
        </Cabecera>
        <Cuerpo>
          {filas.length === 0 && (
            <TablaVacia colSpan={8}>
              {hayFiltros
                ? "Ninguna orden cumple esos filtros."
                : "Todavía no hay órdenes. Las nuevas nacen en borrador."}
            </TablaVacia>
          )}
          {filas.map((orden) => (
            <Fila
              key={orden.id}
              className="cursor-pointer"
              atenuada={orden.estado === "ANULADA"}
              onClick={() => setAbierta(orden.id)}
            >
              <Td>{orden.codigo}</Td>
              <Td numero>
                {orden.fecha_emision !== null ? (
                  comoFecha(orden.fecha_emision)
                ) : (
                  <span
                    style={{ color: "var(--fg-muted)" }}
                    title="Todavía sin factura: se ordena por el día en que se redactó"
                  >
                    —
                  </span>
                )}
              </Td>
              <Td>{orden.proveedor ?? "—"}</Td>
              <Td>{orden.concepto}</Td>
              <Td>
                <div className="flex flex-col items-start gap-1">
                  <Insignia tono={TONO_ESTADO[orden.estado]}>
                    {ESTADOS.find((e) => e.valor === orden.estado)?.etiqueta ?? orden.estado}
                  </Insignia>
                  <PasosOrden estado={orden.estado} />
                </div>
              </Td>
              <Td>
                <Vencimiento orden={orden} />
              </Td>
              <Td derecha numero>
                {orden.monto_base} {orden.divisa ?? ""}
              </Td>
              <Td derecha numero>
                {orden.monto_neto} {orden.divisa ?? ""}
              </Td>
            </Fila>
          ))}
        </Cuerpo>
      </Tabla>

      {/* ⚠ **Una tanda más, no una página.** Un ledger y una bandeja crecen sin
          parar: con páginas numeradas habría que contar la tabla entera en cada
          carga —caro, y más caro cada año— para enseñar un número que a nadie le
          dice nada. Esto dice cuántas se están viendo, que es la pregunta real. */}
      {filas.length >= tope && (
        <div className="flex flex-col items-center gap-1">
          <Boton
            variante="secundario"
            onClick={() => setTope((t) => t + POR_TANDA)}
            disabled={ordenes.isFetching}
          >
            {ordenes.isFetching ? "Trayendo…" : `Ver ${POR_TANDA} más`}
          </Boton>
          <span className="text-xs" style={{ color: "var(--fg-muted)" }}>
            Viendo las {filas.length} más recientes
            {periodo !== null && " de este período"}.
          </span>
        </div>
      )}

      {abierta !== null && <FichaOrden ordenId={abierta} onCerrar={() => setAbierta(null)} />}

      <Modal
        abierto={leyendo}
        titulo="Leer factura con IA"
        descripcion="Se lee, se verifica y, si cuadra, se crea la orden en borrador para revisarla."
        onCerrar={() => setLeyendo(false)}
        ancho="lg"
      >
        {/* Se desmonta al cerrar: la siguiente factura no hereda la lectura anterior. */}
        {leyendo && (
          <LectorFactura
            onAbrir={(ordenId) => {
              clienteQuery.invalidateQueries({ queryKey: ["ordenes-pago"] })
              setLeyendo(false)
              setAbierta(ordenId)
            }}
          />
        )}
      </Modal>

      <Modal
        abierto={creando}
        titulo="Nueva orden de pago"
        descripcion="Nace en borrador: sus servicios se pueden ajustar hasta enviarla a aprobación."
        onCerrar={() => setCreando(false)}
      >
        {/* Se desmonta al cerrar: así una orden nueva no hereda lo elegido en la anterior. */}
        {creando && (
          <FormularioNuevaOrden
            proveedores={proveedores.data ?? []}
            divisas={divisas.data ?? []}
            tasas={tasas.data ?? []}
            categorias={categorias.data ?? []}
            puedeCrearCategoria={tienePermiso(usuario, "gasto:administrar")}
            enviando={crear.isPending}
            error={error}
            onEnviar={(cuerpo) => crear.mutate(cuerpo)}
            onCancelar={() => setCreando(false)}
          />
        )}
      </Modal>
    </section>
  )
}

/** Los cuatro pasos del circuito, en orden. Una anulada o rechazada no avanza por ellos. */
const PASOS: EstadoOrden[] = ["BORRADOR", "EN_APROBACION", "APROBADA", "EJECUTADA"]

/**
 * En qué paso del circuito va la orden, de un vistazo: cuatro marcas, rellenas hasta
 * la suya.
 *
 * ⚠ **Acompaña a la insignia, no la sustituye.** La insignia dice el nombre; esto dice
 * cuánto camino queda. Sin la insignia sería un dibujo que hay que aprender a leer.
 */
function PasosOrden({ estado }: { estado: EstadoOrden }) {
  const actual = PASOS.indexOf(estado)
  if (actual < 0) return null
  return (
    <span
      className="flex gap-[2px]"
      role="img"
      aria-label={`Paso ${actual + 1} de ${PASOS.length} del circuito`}
    >
      {PASOS.map((paso, i) => (
        <span
          key={paso}
          className="h-1 w-4 rounded-full"
          style={{ backgroundColor: i <= actual ? "var(--primary)" : "var(--cristal-filo)" }}
        />
      ))}
    </span>
  )
}

/**
 * Cuándo vence la factura, dicho como urgencia: «Venció hace 3 d», «En 2 días».
 *
 * ⚠ **Sólo alarma lo que sigue vivo.** A una orden pagada o anulada no se le dice
 * «venció hace 20 días»: ese vencimiento ya no le importa a nadie, y pintarlo en rojo
 * haría que el rojo de las que sí importan se pierda entre los demás.
 */
function Vencimiento({ orden }: { orden: Orden }) {
  if (!orden.fecha_vencimiento) {
    return <span style={{ color: "var(--fg-muted)" }}>—</span>
  }
  const cerrada = orden.estado === "EJECUTADA" || orden.estado === "ANULADA"
  const urgencia = urgenciaDe(orden.fecha_vencimiento)
  return (
    <div className="flex flex-col items-start gap-0.5">
      <span className="tabular-nums">{comoFecha(orden.fecha_vencimiento)}</span>
      {!cerrada && urgencia.tono !== "neutro" && (
        <Insignia tono={urgencia.tono}>{urgencia.texto}</Insignia>
      )}
    </div>
  )
}

/** Lo que la orden necesita saber de un gasto del catálogo para precargarse. */
type GastoDelProveedor = {
  id: number
  codigo: string
  nombre: string
  categoria_id: number | null
  divisa_id: number | null
  divisa: string | null
  monto_referencia: string | null
}

/**
 * Qué se compra en una línea del alta: servicio, consumible o bien.
 *
 * ⚠ **Elegir consumible o bien ata la línea a un artículo**, y de ahí sale también su
 * descripción: el artículo ES el concepto. Si no existe, se crea desde aquí — mandar
 * a alguien al inventario a dar de alta la resma en mitad de una factura es lo que
 * hace que la factura acabe sin desglosar.
 */
function QueEsLaLinea({
  indice,
  articuloId,
  usoInmediato,
  descripcion,
  exento,
  onCambiar,
}: {
  indice: number
  articuloId: number | null
  usoInmediato: boolean
  descripcion: string
  exento: boolean
  onCambiar: (cambios: Partial<LineaBorrador>) => void
}) {
  const usuario = usarSesion((e) => e.usuario)
  const [naturaleza, setNaturaleza] = useState<Naturaleza>(
    articuloId !== null ? "CONSUMIBLE" : usoInmediato ? "MATERIAL" : "SERVICIO",
  )
  const [creando, setCreando] = useState(false)
  const esArticulo = naturaleza === "CONSUMIBLE" || naturaleza === "BIEN"
  const puedeCrear = tienePermiso(usuario, "inventario:administrar")

  const articulos = useQuery({
    queryKey: ["articulos", naturaleza],
    queryFn: () => api.get<ArticuloBreve[]>(`/api/v1/articulos?clase=${naturaleza}`),
    enabled:
      esArticulo &&
      (tienePermiso(usuario, "inventario:ver") || tienePermiso(usuario, "orden_pago:crear")),
  })

  return (
    <div className="grid gap-2 sm:grid-cols-[auto_1fr]">
      <select
        aria-label={`Qué se compra en la línea ${indice + 1}`}
        value={naturaleza}
        onChange={(e) => {
          const elegida = e.target.value as Naturaleza
          setNaturaleza(elegida)
          onCambiar({ articulo_id: null, uso_inmediato: elegida === "MATERIAL" })
        }}
        className="campo"
      >
        {NATURALEZAS.map((n) => (
          <option key={n.valor} value={n.valor}>
            {n.etiqueta}
          </option>
        ))}
      </select>
      {esArticulo && (
        <SelectorBuscable
          id={`linea-articulo-${indice}`}
          opciones={(articulos.data ?? []).map((a) => ({
            valor: a.id,
            etiqueta: a.nombre,
            detalle: a.codigo,
          }))}
          valor={articuloId}
          onCambiar={(id) => {
            const elegido = (articulos.data ?? []).find((a) => a.id === id)
            onCambiar({
              articulo_id: id,
              // La descripción sale del artículo, que es el concepto.
              descripcion: elegido?.nombre ?? descripcion,
              // Y lo exento también: es de la cosa comprada, no de esta factura.
              ...(elegido ? { exento_iva: elegido.exento_iva } : {}),
            })
          }}
          vacio={naturaleza === "BIEN" ? "Elige el bien" : "Elige el consumible"}
          placeholder="Nombre o código…"
        />
      )}
      {/* ⚠ **Se crea con el formulario COMPLETO, el mismo del inventario.** El atajo
          que creaba el artículo sólo con el nombre dejaba sin categoría de gasto y sin
          unidad todo lo que se daba de alta desglosando una factura, que es cuándo se
          da de alta de verdad: el informe por categoría no lo agrupaba después. */}
      {esArticulo && puedeCrear && !creando && (
        <Boton tamano="sm" onClick={() => setCreando(true)}>
          <Plus size={14} /> {naturaleza === "BIEN" ? "Crear bien" : "Crear consumible"}
        </Boton>
      )}
      {creando && (
        <div className="tarjeta p-3 sm:col-span-2">
          <FormularioArticulo
            articulo={null}
            clase={naturaleza as "CONSUMIBLE" | "BIEN"}
            nombreSugerido={descripcion}
            onCerrar={() => setCreando(false)}
            onGuardado={(creado: Articulo) => {
              setCreando(false)
              // El artículo ES el concepto de la línea, y su ficha dice si paga IVA.
              onCambiar({
                articulo_id: creado.id,
                descripcion: creado.nombre,
                exento_iva: creado.exento_iva,
              })
            }}
          />
        </div>
      )}
      {/* ⚠ Aquí y no sólo en la ficha: obligar a crear la orden y entrar a corregir
          qué renglón va exento es la misma trampa que la naturaleza de la línea — la
          factura ya está cargada, y entonces no se corrige. */}
      <label className="flex items-center gap-2 text-xs sm:col-span-2">
        <input
          type="checkbox"
          checked={exento}
          onChange={(e) => onCambiar({ exento_iva: e.target.checked })}
        />
        <span>
          Exento de IVA
          {esArticulo && articuloId !== null && (
            <span style={{ color: "var(--fg-muted)" }}> · viene de la ficha del artículo</span>
          )}
        </span>
      </label>
    </div>
  )
}

/** Una línea mientras se redacta: todo en texto, tal como se teclea. */
type LineaBorrador = {
  clave: number
  gasto_id: number | null
  descripcion: string
  cantidad: string
  precio: string
  /** La divisa en la que está la referencia del gasto, para avisar si no es la de la orden. */
  divisaReferencia: number | null
  /**
   * Qué se compra en esta línea. Con artículo entra al depósito al pagar; sin él es
   * un servicio.
   *
   * ⚠ **Se decide AQUÍ y no después.** Obligar a crear la orden y entrar a su ficha
   * para decir qué era cada concepto significa que casi nunca se dice: la factura ya
   * está cargada y el trabajo, hecho.
   */
  articulo_id: number | null
  /** Material que se usa al comprarlo: sin artículo y sin pasar por el depósito. */
  uso_inmediato: boolean
  /**
   * Si este renglón paga IVA.
   *
   * ⚠ **Por renglón y no por factura.** Una compra de papelería trae el papel gravado
   * y los libros exentos en el mismo documento: con la casilla sólo en la orden, marcar
   * IVA le aplica el 16 % a los libros e infla el total — y el total es lo que se
   * transfiere. Con artículo llega heredado de su ficha.
   */
  exento_iva: boolean
}

type ArticuloBreve = { id: number; codigo: string; nombre: string; exento_iva: boolean }

/** Las tres cosas que puede ser una línea. La misma pregunta que en la ficha. */
const NATURALEZAS = [
  { valor: "SERVICIO", etiqueta: "Servicio" },
  // ⚠ Los tornillos y la toma eléctrica de una reparación menor: se compran y se
  // instalan el mismo día. Como consumible entrarían al depósito con una existencia
  // que nadie guarda ni cuenta, y el conteo físico no cuadraría nunca.
  { valor: "MATERIAL", etiqueta: "Material de uso inmediato" },
  { valor: "CONSUMIBLE", etiqueta: "Consumible" },
  { valor: "BIEN", etiqueta: "Activo o bien" },
] as const

type Naturaleza = (typeof NATURALEZAS)[number]["valor"]

let siguienteClave = 1

/**
 * El alta de una orden: proveedor → sus servicios, con cantidad y valor → la orden.
 *
 * ⚠ **El gasto es lo que enlaza el catálogo, el proveedor y la orden.** Al elegir
 * proveedor se ofrecen SUS gastos; cada uno que se elige entra como una línea con su
 * monto de referencia, y la cantidad y el valor se ajustan ahí mismo. El primero
 * además propone el concepto, la categoría y la moneda. Todo queda editable.
 *
 * ⚠ **La moneda es de la ORDEN, no de cada línea.** Una orden es un pago a un
 * proveedor en una moneda: es lo que tesorería debitará de UNA cuenta. Si un servicio
 * tiene su referencia en otra moneda, su línea lo dice y el valor se ajusta a mano:
 * convertirlo solo sería elegir una tasa sin que nadie lo decida.
 *
 * ⚠ **Las líneas viajan con la orden y se crean en la misma transacción.** Una orden
 * que nace con dos de sus tres servicios parece completa y no lo está.
 *
 * ⚠ **Cambiar de proveedor vacía los servicios del catálogo.** El servidor rechaza con
 * un 409 el gasto de otro proveedor; conservarlos sería preparar ese rechazo. Las
 * líneas libres —sin gasto— se quedan: no son de nadie.
 */
/**
 * Lo mismo que `alcance_ordenes.normalizar_expediente` del servidor, sólo para
 * ENSEÑAR cómo quedará y no dejar enviar algo que va a rechazar. Quien guarda es el
 * servidor: lo que se manda es lo tecleado.
 */
const EXPEDIENTE = /^[A-Za-z0-9][A-Za-z0-9/-]{0,19}$/

/**
 * Todos los proveedores activos de una categoría. El servidor sirve 100 por página
 * (`normalizar_limite`) y los operativos pueden pasar de cien: con una sola página, la
 * almacenadora que cae en la 101 sencillamente no saldría en la lista.
 */
async function todosLosProveedores(categoria: string): Promise<Proveedor[]> {
  const todos: Proveedor[] = []
  for (let desde = 0; desde < 2000; desde += 100) {
    const pagina = await api.get<Proveedor[]>(
      `/api/v1/proveedores?limit=100&offset=${desde}&activo=true&categoria=${categoria}`,
    )
    todos.push(...pagina)
    if (pagina.length < 100) break
  }
  return todos
}

/** Los de las dos listas sin repetir —un proveedor puede ser de las dos—, por nombre. */
function unirProveedores(a: Proveedor[], b: Proveedor[]): Proveedor[] {
  const porId = new Map([...a, ...b].map((p) => [p.id, p]))
  return [...porId.values()].sort((x, y) => x.nombre.localeCompare(y.nombre, "es"))
}
function limpiarExpediente(texto: string): string {
  return texto
    .trim()
    .replace(/^exp(?:ediente)?\.?\s*/i, "")
    .trim()
    .toUpperCase()
}

function FormularioNuevaOrden({
  proveedores,
  divisas,
  tasas,
  categorias,
  puedeCrearCategoria,
  enviando,
  error,
  onEnviar,
  onCancelar,
}: {
  proveedores: Proveedor[]
  divisas: Divisa[]
  tasas: TasaVigente[]
  categorias: Categoria[]
  puedeCrearCategoria: boolean
  enviando: boolean
  error: string | null
  onEnviar: (cuerpo: Record<string, unknown>) => void
  onCancelar: () => void
}) {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const [proveedorId, setProveedorId] = useState<number | null>(null)
  const [lineas, setLineas] = useState<LineaBorrador[]>([])
  const [concepto, setConcepto] = useState("")
  const [divisaId, setDivisaId] = useState<number | null>(
    // El dólar por omisión: es la divisa de referencia del sistema y la de casi todo
    // el gasto administrativo.
    divisas.find((d) => d.codigo === "USD")?.id ?? null,
  )
  const [categoriaId, setCategoriaId] = useState<number | null>(null)
  // El expediente del 2.0 al que pertenece el gasto. Va DELANTE de la descripción
  // («EXP 2026119 · …») y lo compone el servidor: ver `services/alcance_ordenes.py`.
  const [vinculada, setVinculada] = useState(false)
  const [expediente, setExpediente] = useState("")
  const numeroExpediente = limpiarExpediente(expediente)
  const expedienteValido = !vinculada || EXPEDIENTE.test(numeroExpediente)
  // Lo que se le suma a la descripción, para que el tope de 200 lo diga el campo y
  // no un 422 al guardar.
  const prefijo = vinculada && numeroExpediente !== "" ? `EXP ${numeroExpediente} · ` : ""
  // Con expediente, también los OPERATIVOS. Se piden sólo al marcar la casilla: una
  // orden de la agencia no los admite y el servidor la rechazaría.
  const operativos = useQuery({
    queryKey: ["proveedores", "OPERATIVO"],
    queryFn: () => todosLosProveedores("OPERATIVO"),
    enabled: vinculada,
  })
  const elegibles = vinculada ? unirProveedores(proveedores, operativos.data ?? []) : proveedores

  function cambiarVinculada(marcada: boolean) {
    setVinculada(marcada)
    // Desmarcar con un operativo elegido dejaría una orden que el servidor no admite.
    if (!marcada && proveedorId !== null && !proveedores.some((p) => p.id === proveedorId)) {
      elegirProveedor(null)
    }
  }

  const puedeVerGastos =
    tienePermiso(usuario, "gasto:ver") || tienePermiso(usuario, "orden_pago:crear")
  const gastos = useQuery({
    queryKey: ["gastos", "proveedor", proveedorId],
    queryFn: () =>
      api.get<GastoDelProveedor[]>(
        `/api/v1/gastos?proveedor_id=${proveedorId}&activo=true&limit=100`,
      ),
    enabled: proveedorId !== null && puedeVerGastos,
  })
  const codigoDe = new Map(divisas.map((d) => [d.id, d.codigo]))
  // Qué línea tiene abierto el conversor. Una cada vez: abrirlos todos llenaría el
  // formulario de campos que nadie está mirando.
  const [convirtiendo, setConvirtiendo] = useState<number | null>(null)
  const monedasConversor = monedasDeTasas(tasas)

  function elegirProveedor(id: number | null) {
    if (id !== proveedorId) setLineas((actuales) => actuales.filter((l) => l.gasto_id === null))
    setProveedorId(id)
  }

  function anadirGasto(gasto: GastoDelProveedor) {
    // El primero propone lo de la orden; los siguientes sólo suman su línea.
    if (lineas.length === 0) {
      setConcepto((actual) => (actual.trim() === "" ? gasto.nombre : actual))
      if (gasto.divisa_id !== null) setDivisaId(gasto.divisa_id)
      if (gasto.categoria_id !== null) setCategoriaId(gasto.categoria_id)
    }
    setLineas((actuales) => [
      ...actuales,
      {
        clave: siguienteClave++,
        gasto_id: gasto.id,
        descripcion: gasto.nombre,
        cantidad: "1",
        precio: gasto.monto_referencia ?? "",
        divisaReferencia: gasto.divisa_id,
        articulo_id: null,
        uso_inmediato: false,
        exento_iva: false,
      },
    ])
  }

  function anadirLibre() {
    setLineas((actuales) => [
      ...actuales,
      {
        clave: siguienteClave++,
        gasto_id: null,
        descripcion: "",
        cantidad: "1",
        precio: "",
        divisaReferencia: null,
        articulo_id: null,
        uso_inmediato: false,
        exento_iva: false,
      },
    ])
  }

  function cambiarLinea(clave: number, cambios: Partial<LineaBorrador>) {
    setLineas((actuales) => actuales.map((l) => (l.clave === clave ? { ...l, ...cambios } : l)))
  }

  async function crearGasto(nombre: string): Promise<number> {
    const nuevo = await api.post<GastoDelProveedor>("/api/v1/gastos", {
      nombre,
      proveedor_id: proveedorId,
      categoria_id: categoriaId,
      divisa_id: divisaId,
      tipo_gasto: "VARIABLE",
    })
    await clienteQuery.invalidateQueries({ queryKey: ["gastos"] })
    // Con lo TECLEADO y no con `nuevo.nombre`: si la respuesta no lo trajera, la
    // línea quedaría sin descripción y el formulario reventaría al validarla.
    anadirGasto({ ...nuevo, nombre, divisa_id: divisaId, monto_referencia: null })
    return nuevo.id
  }

  const subtotales = lineas.map((l) => subtotalCentimos(l.cantidad, l.precio))
  const lineasValidas =
    lineas.length > 0 &&
    lineas.every((l, i) => l.descripcion.trim().length >= 2 && subtotales[i] !== null)
  const total = subtotales.reduce<bigint>((suma, s) => suma + (s ?? 0n), 0n)
  // ⚠ **La categoría es obligatoria desde aquí**, y el servidor la vuelve a exigir al
  // enviar a aprobación: una orden sin ella desaparece del informe por categoría y del
  // tope de su partida **sin decir nada**, con el total cuadrando igual. Esconder el
  // botón no es la regla —la regla está en `ordenes_pago.enviar_a_aprobacion`—, pero
  // pedirla aquí evita redactar algo que se va a atascar.
  const listo =
    proveedorId !== null &&
    divisaId !== null &&
    categoriaId !== null &&
    concepto.trim().length >= 3 &&
    prefijo.length + concepto.trim().length <= 200 &&
    expedienteValido &&
    lineasValidas

  function enviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const datos = new FormData(evento.currentTarget)
    const fecha = String(datos.get("fecha_requerida") ?? "").trim()
    onEnviar({
      proveedor_id: proveedorId,
      concepto: concepto.trim(),
      divisa_id: divisaId,
      categoria_id: categoriaId,
      fecha_requerida: fecha === "" ? null : fecha,
      expediente_numero: vinculada ? expediente.trim() : null,
      lineas: lineas.map((l) => ({
        descripcion: l.descripcion.trim(),
        cantidad: cantidadValida(l.cantidad),
        precio_unitario: precioLineaValido(l.precio),
        gasto_id: l.gasto_id,
        articulo_id: l.articulo_id,
        uso_inmediato: l.uso_inmediato,
        exento_iva: l.exento_iva,
      })),
    })
  }

  const moneda = divisaId !== null ? (codigoDe.get(divisaId) ?? "") : ""

  return (
    <form className="space-y-4" onSubmit={enviar}>
      {/* ⚠ **La casilla existe para que se DECLARE, no para adornar.** Hasta ahora el
          expediente se escribía a mano en la descripción, cada vez de una forma
          —«EXP 2026119», «exp 2026119», al final o al principio—, y así no se puede
          buscar. Marcada, pide el número y el servidor lo pone delante con un formato
          fijo.

          ⚠ **Va ANTES del proveedor, y no por estética**: marcada, la lista de
          proveedores suma los OPERATIVOS —la almacenadora, la naviera—, que una orden
          de la agencia no admite. */}
      <div className="space-y-2">
        <label className="flex min-h-11 items-center gap-2 text-sm sm:min-h-0">
          <input
            type="checkbox"
            checked={vinculada}
            onChange={(e) => cambiarVinculada(e.target.checked)}
            className="size-4"
          />
          Vinculada a un expediente
        </label>
        {vinculada && (
          <Campo
            para="orden-expediente"
            etiqueta="N.º de expediente"
            ayuda={
              prefijo !== "" && concepto.trim() !== ""
                ? `Se guardará como «${prefijo}${concepto.trim()}».`
                : "Se añade delante de la descripción, como «EXP 2026119 · …»."
            }
          >
            <input
              id="orden-expediente"
              value={expediente}
              onChange={(e) => setExpediente(e.target.value)}
              placeholder="2026119"
              maxLength={40}
              required
              // biome-ignore lint/a11y/noAutofocus: aparece al marcar la casilla; el foco va a lo que se acaba de pedir
              autoFocus
              aria-invalid={expediente.trim() !== "" && !expedienteValido}
              className="campo mt-1"
            />
            {expediente.trim() !== "" && !expedienteValido && (
              <p className="mt-1 text-xs" style={{ color: "var(--destructive)" }}>
                Hasta 20 caracteres: letras, dígitos, guion o barra.
              </p>
            )}
          </Campo>
        )}
      </div>
      <Campo
        para="orden-proveedor"
        etiqueta="Proveedor"
        ayuda={
          vinculada
            ? "Administrativo u operativo: la orden es de un expediente."
            : "Sólo administrativos: lo operativo se paga marcando la orden como de un expediente."
        }
      >
        <SelectorBuscable
          id="orden-proveedor"
          opciones={elegibles.map((p) => ({
            valor: p.id,
            etiqueta: p.nombre,
            detalle: p.rif ?? undefined,
          }))}
          valor={proveedorId}
          onCambiar={elegirProveedor}
          placeholder="Nombre o RIF…"
        />
      </Campo>

      <fieldset className="space-y-3">
        <legend className="text-sm font-medium">Servicios</legend>

        {/* Lo que ofrece y a qué precio, a la vista al elegirlo (04/11). El desplegable
            de abajo sigue sirviendo para buscar entre muchos y para crear uno nuevo. */}
        {proveedorId !== null && puedeVerGastos && (
          <ServiciosParaAnadir
            servicios={gastos.data ?? []}
            enLineas={lineas.reduce(
              (veces, l) =>
                l.gasto_id === null
                  ? veces
                  : veces.set(l.gasto_id, (veces.get(l.gasto_id) ?? 0) + 1),
              new Map<number, number>(),
            )}
            onAnadir={(servicio) => {
              const gasto = (gastos.data ?? []).find((g) => g.id === servicio.id)
              if (gasto) anadirGasto(gasto)
            }}
          />
        )}

        {proveedorId !== null && puedeVerGastos && (
          <Campo
            para="orden-servicio"
            etiqueta="Añadir servicio"
            ayuda={
              gastos.data && gastos.data.length === 0
                ? "Este proveedor no tiene servicios en el catálogo. Escribe uno para crearlo."
                : "Busca entre sus servicios o escribe uno nuevo para crearlo."
            }
          >
            <SelectorBuscable
              id="orden-servicio"
              opciones={(gastos.data ?? []).map((g) => ({
                valor: g.id,
                etiqueta: g.nombre,
                detalle: g.monto_referencia ? `${g.monto_referencia} ${g.divisa ?? ""}` : g.codigo,
              }))}
              // Siempre vacío: elegir uno lo AÑADE como línea, no lo deja elegido.
              valor={null}
              onCambiar={(id) => {
                const gasto = (gastos.data ?? []).find((g) => g.id === id)
                if (gasto) anadirGasto(gasto)
              }}
              placeholder="Buscar servicio…"
              queSeCrea="servicio"
              onCrear={tienePermiso(usuario, "gasto:administrar") ? crearGasto : undefined}
            />
          </Campo>
        )}

        {lineas.length > 0 && (
          <ul className="space-y-2">
            {lineas.map((linea, i) => {
              const subtotal = subtotales[i]
              const otraMoneda =
                linea.divisaReferencia !== null &&
                divisaId !== null &&
                linea.divisaReferencia !== divisaId
              return (
                <li key={linea.clave} className="tarjeta space-y-2 p-3">
                  {/* ⚠ **Qué es se dice aquí, al redactar.** Obligar a crear la orden
                      y entrar a su ficha para clasificar los conceptos significa que
                      casi nunca se clasifican: la factura ya está cargada. */}
                  <QueEsLaLinea
                    indice={i}
                    articuloId={linea.articulo_id}
                    usoInmediato={linea.uso_inmediato}
                    descripcion={linea.descripcion}
                    exento={linea.exento_iva}
                    onCambiar={(cambios) => cambiarLinea(linea.clave, cambios)}
                  />
                  <div className="flex items-center gap-2">
                    <input
                      aria-label={`Descripción de la línea ${i + 1}`}
                      value={linea.descripcion}
                      onChange={(e) => cambiarLinea(linea.clave, { descripcion: e.target.value })}
                      placeholder="Descripción"
                      maxLength={250}
                      // Con artículo, el nombre del artículo ES el concepto: dos
                      // campos para lo mismo acaban diciendo cosas distintas.
                      disabled={linea.articulo_id !== null}
                      className="campo flex-1"
                    />
                    <button
                      type="button"
                      aria-label={`Quitar la línea ${i + 1}`}
                      onClick={() =>
                        setLineas((actuales) => actuales.filter((l) => l.clave !== linea.clave))
                      }
                      className="foco-cristal toque-cristal inline-flex size-9 items-center justify-center rounded-[var(--radius-control)]"
                    >
                      <Trash2 size={16} aria-hidden={true} />
                    </button>
                  </div>
                  <div className="grid grid-cols-[1fr_1.5fr_auto] items-center gap-2">
                    <input
                      aria-label={`Cantidad de la línea ${i + 1}`}
                      value={linea.cantidad}
                      onChange={(e) => cambiarLinea(linea.clave, { cantidad: e.target.value })}
                      inputMode="decimal"
                      className="campo tabular-nums"
                    />
                    <input
                      aria-label={`Valor unitario de la línea ${i + 1}`}
                      value={linea.precio}
                      onChange={(e) => cambiarLinea(linea.clave, { precio: e.target.value })}
                      inputMode="decimal"
                      placeholder="Valor"
                      className="campo tabular-nums"
                    />
                    <span className="min-w-24 text-right text-sm tabular-nums">
                      {subtotal == null ? "—" : `${comoMonto(subtotal)} ${moneda}`}
                    </span>
                  </div>
                  {/* ⚠ El valor se teclea en la moneda que uno SEPA. El proveedor
                      cotiza en euros y la factura llega en bolívares; convertir a
                      mano antes de escribir es hacer con la calculadora del teléfono
                      justo la cuenta que se equivoca. */}
                  <div>
                    <Boton
                      tamano="sm"
                      onClick={() =>
                        setConvirtiendo(convirtiendo === linea.clave ? null : linea.clave)
                      }
                    >
                      <ArrowLeftRight size={14} />
                      {convirtiendo === linea.clave ? "Ocultar equivalencias" : "Convertir"}
                    </Boton>
                  </div>
                  {convirtiendo === linea.clave && moneda !== "" && (
                    <Conversor
                      monedas={monedasConversor}
                      principal={moneda}
                      valor={linea.precio}
                      onCambiar={(v) => cambiarLinea(linea.clave, { precio: v })}
                      etiqueta={`Valor unitario en otra moneda · línea ${i + 1}`}
                    />
                  )}
                  {otraMoneda && (
                    <p className="text-xs" style={{ color: "var(--warning)" }}>
                      Su referencia está en {codigoDe.get(linea.divisaReferencia ?? 0) ?? "otra"} y
                      la orden cobra en {moneda}: ajusta el valor.
                    </p>
                  )}
                </li>
              )
            })}
          </ul>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <Boton tamano="sm" onClick={anadirLibre}>
            <Plus size={14} /> Concepto libre
          </Boton>
          {lineas.length > 0 && (
            <p className="text-sm">
              Base: <strong className="tabular-nums">{comoMonto(total)}</strong> {moneda}
            </p>
          )}
        </div>
      </fieldset>

      <Campo
        para="orden-concepto"
        etiqueta="Descripción"
        ayuda="Con qué se reconoce la orden en el listado: «Factura N.º 5615616». Lo que se compró va en los conceptos de arriba."
      >
        <input
          id="orden-concepto"
          value={concepto}
          onChange={(e) => setConcepto(e.target.value)}
          required
          minLength={3}
          maxLength={200 - prefijo.length}
          className="campo mt-1"
        />
      </Campo>

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo para="orden-divisa" etiqueta="Moneda de cobro">
          <SelectorBuscable
            id="orden-divisa"
            opciones={divisas.map((d) => ({ valor: d.id, etiqueta: d.codigo, detalle: d.nombre }))}
            valor={divisaId}
            onCambiar={setDivisaId}
          />
        </Campo>
        <Campo
          para="orden-categoria"
          etiqueta="Categoría"
          ayuda={
            puedeCrearCategoria
              ? "Obligatoria: sin ella la orden no entra en el informe por categoría ni consume su partida. Si no existe, créala desde aquí."
              : "Obligatoria: sin ella la orden no entra en el informe por categoría ni consume su partida."
          }
        >
          <SelectorBuscable
            id="orden-categoria"
            opciones={categorias
              .filter((c) => c.activo)
              .map((c) => ({ valor: c.id, etiqueta: c.nombre }))}
            valor={categoriaId}
            onCambiar={setCategoriaId}
            vacio="Elige la categoría"
            queSeCrea="categoría"
            onCrear={
              puedeCrearCategoria
                ? async (nombre) => {
                    const id = await crearCategoria(nombre, "VARIABLE")
                    await clienteQuery.invalidateQueries({ queryKey: ["categorias-gasto"] })
                    return id
                  }
                : undefined
            }
          />
        </Campo>
      </div>
      <Campo para="orden-fecha" etiqueta="Fecha requerida">
        <input id="orden-fecha" name="fecha_requerida" type="date" className="campo mt-1" />
      </Campo>

      {error && (
        <p className="text-sm" style={{ color: "var(--destructive)" }}>
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Boton onClick={onCancelar}>Cancelar</Boton>
        <Boton type="submit" variante="primario" disabled={enviando || !listo}>
          {enviando ? "Creando…" : "Crear"}
        </Boton>
      </div>
    </form>
  )
}
